// ---------------------------------------------------------------------------
// C2: Student Progress Tracking + Completion Events
// ---------------------------------------------------------------------------
// Per-student: track lesson completions, module completions, course completion.
// Completion criteria: video ≥90%, quiz passed, exercise submitted.
// Events emitted: course.lesson.completed, course.module.completed,
// course.completed — wire to workflow triggers (certificate, review, upsell).
// ---------------------------------------------------------------------------

import { isVideoComplete } from "../renderer/components/video-player.js";
import type { PageType } from "../schema/index.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LessonCompletionCriteria {
  lessonType: "video" | "text" | "quiz" | "exercise" | "pdf";
  /** For video: % watched threshold */
  videoThreshold?: number;
  /** For quiz: minimum passing score */
  quizPassingScore?: number;
  /** For exercise: whether submission is required */
  exerciseRequired?: boolean;
}

export interface LessonProgress {
  lessonId: string;
  lessonType: LessonCompletionCriteria["lessonType"];
  isComplete: boolean;
  completedAt: Date | null;
  /** Video: % watched */
  videoPercent?: number;
  /** Quiz: score achieved */
  quizScore?: number;
  /** Exercise: whether submitted */
  exerciseSubmitted?: boolean;
}

export interface ModuleProgress {
  moduleId: string;
  totalLessons: number;
  completedLessons: number;
  progressPercent: number;
  isComplete: boolean;
}

export interface CourseProgress {
  courseId: string;
  enrollmentId: string;
  totalModules: number;
  completedModules: number;
  totalLessons: number;
  completedLessons: number;
  progressPercent: number;
  isComplete: boolean;
  modules: ModuleProgress[];
}

export interface ProgressEvent {
  type: "course.lesson.completed" | "course.module.completed" | "course.completed";
  tenantId: string;
  courseId: string;
  enrollmentId: string;
  contactId: string;
  /** Lesson/module/course ID that was completed */
  entityId: string;
  progressPercent: number;
  timestamp: number;
}

export const COURSE_ENGINE_OUTPUT_PAGE_TYPES = [
  "course_lesson",
  "course_exercise",
  "certificate",
] as const satisfies readonly PageType[];

export type CourseEngineOutputPageType =
  (typeof COURSE_ENGINE_OUTPUT_PAGE_TYPES)[number];

export type CourseEngineOutputKind = "lesson" | "certificate";

export interface CourseEngineOutputTarget {
  kind: CourseEngineOutputKind;
  pageType: CourseEngineOutputPageType;
  pageId: string | null;
  title: string;
  courseId: string;
  moduleId?: string;
  lessonId?: string;
  lessonType?: LessonCompletionCriteria["lessonType"];
}

export function resolveLessonOutputPageType(
  lessonType: LessonCompletionCriteria["lessonType"],
): Extract<CourseEngineOutputPageType, "course_lesson" | "course_exercise"> {
  return lessonType === "exercise" ? "course_exercise" : "course_lesson";
}

export function buildLessonOutputTarget(input: {
  courseId: string;
  moduleId: string;
  lessonId: string;
  lessonType: LessonCompletionCriteria["lessonType"];
  title: string;
  pageId: string | null;
}): CourseEngineOutputTarget {
  return {
    kind: "lesson",
    pageType: resolveLessonOutputPageType(input.lessonType),
    pageId: input.pageId,
    title: input.title,
    courseId: input.courseId,
    moduleId: input.moduleId,
    lessonId: input.lessonId,
    lessonType: input.lessonType,
  };
}

export function buildCertificateOutputTarget(input: {
  courseId: string;
  title: string;
  pageId: string | null;
}): CourseEngineOutputTarget {
  return {
    kind: "certificate",
    pageType: "certificate",
    pageId: input.pageId,
    title: input.title,
    courseId: input.courseId,
  };
}

// ---------------------------------------------------------------------------
// Completion Evaluation
// ---------------------------------------------------------------------------

/**
 * Check if a lesson is complete based on its type and activity data.
 */
export function isLessonComplete(
  criteria: LessonCompletionCriteria,
  activity: { videoPercent?: number; quizScore?: number; exerciseSubmitted?: boolean },
): boolean {
  switch (criteria.lessonType) {
    case "video":
      return isVideoComplete(activity.videoPercent ?? 0, criteria.videoThreshold ?? 90);
    case "quiz":
      return (activity.quizScore ?? 0) >= (criteria.quizPassingScore ?? 70);
    case "exercise":
      return criteria.exerciseRequired !== false ? (activity.exerciseSubmitted ?? false) : true;
    case "text":
    case "pdf":
      return true; // Text/PDF lessons are complete once opened
    default:
      return false;
  }
}

/**
 * Compute module progress from lesson completions.
 */
export function computeModuleProgress(
  moduleId: string,
  lessons: LessonProgress[],
): ModuleProgress {
  const total = lessons.length;
  const completed = lessons.filter(l => l.isComplete).length;
  return {
    moduleId,
    totalLessons: total,
    completedLessons: completed,
    progressPercent: total > 0 ? Math.round((completed / total) * 100) : 0,
    isComplete: total > 0 && completed === total,
  };
}

/**
 * Compute course progress from all module progresses.
 */
export function computeCourseProgress(
  courseId: string,
  enrollmentId: string,
  modules: Array<{ moduleId: string; lessons: LessonProgress[] }>,
): CourseProgress {
  const moduleProgresses = modules.map(m => computeModuleProgress(m.moduleId, m.lessons));
  const totalModules = moduleProgresses.length;
  const completedModules = moduleProgresses.filter(m => m.isComplete).length;
  const totalLessons = moduleProgresses.reduce((s, m) => s + m.totalLessons, 0);
  const completedLessons = moduleProgresses.reduce((s, m) => s + m.completedLessons, 0);

  return {
    courseId,
    enrollmentId,
    totalModules,
    completedModules,
    totalLessons,
    completedLessons,
    progressPercent: totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0,
    isComplete: totalModules > 0 && completedModules === totalModules,
    modules: moduleProgresses,
  };
}

/**
 * Generate progress events based on state change.
 * Compare previous and current progress to determine which events to emit.
 */
export function generateProgressEvents(
  tenantId: string,
  contactId: string,
  previous: CourseProgress | null,
  current: CourseProgress,
  /** The specific lesson that was just completed (if any) */
  completedLessonId?: string,
): ProgressEvent[] {
  const events: ProgressEvent[] = [];
  const now = Date.now();

  // Emit lesson.completed for the specific lesson
  if (completedLessonId) {
    events.push({
      type: "course.lesson.completed",
      tenantId, courseId: current.courseId, enrollmentId: current.enrollmentId,
      contactId, entityId: completedLessonId,
      progressPercent: current.progressPercent, timestamp: now,
    });
  }

  // Check for newly completed modules
  if (previous) {
    const prevCompletedModuleIds = new Set(
      previous.modules.filter(m => m.isComplete).map(m => m.moduleId),
    );

    for (const mod of current.modules) {
      if (mod.isComplete && !prevCompletedModuleIds.has(mod.moduleId)) {
        events.push({
          type: "course.module.completed",
          tenantId, courseId: current.courseId, enrollmentId: current.enrollmentId,
          contactId, entityId: mod.moduleId,
          progressPercent: current.progressPercent, timestamp: now,
        });
      }
    }
  }

  // Check for course completion
  if (current.isComplete && (!previous || !previous.isComplete)) {
    events.push({
      type: "course.completed",
      tenantId, courseId: current.courseId, enrollmentId: current.enrollmentId,
      contactId, entityId: current.courseId,
      progressPercent: 100, timestamp: now,
    });
  }

  return events;
}

// ---------------------------------------------------------------------------
// C3: Certificate Template Variables
// ---------------------------------------------------------------------------

export interface CertificateVariables {
  studentName: string;
  courseName: string;
  completionDate: string;
  instructor?: string;
  certificateId: string;
  verificationUrl: string;
}

/**
 * Build certificate template variables from enrollment + course data.
 */
export function buildCertificateVariables(input: {
  studentName: string;
  courseName: string;
  completedAt: Date;
  instructor?: string;
  certificateToken: string;
  baseUrl: string;
}): CertificateVariables {
  return {
    studentName: input.studentName,
    courseName: input.courseName,
    completionDate: input.completedAt.toLocaleDateString("en-US", {
      year: "numeric", month: "long", day: "numeric",
    }),
    instructor: input.instructor,
    certificateId: input.certificateToken,
    verificationUrl: `${input.baseUrl}/verify/${input.certificateToken}`,
  };
}

/**
 * Replace template variables in a string.
 * {{studentName}} → actual name, etc.
 */
export function interpolateTemplate(template: string, variables: CertificateVariables): string {
  let result = template;
  for (const [key, value] of Object.entries(variables)) {
    if (value !== undefined) {
      result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, "g"), String(value));
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// C4: Enrollment Types
// ---------------------------------------------------------------------------

export const ENROLLMENT_SOURCES = [
  "manual",     // Admin adds student
  "form",       // Student fills enrollment form
  "workflow",   // Automated workflow enrolls
  "purchase",   // Student purchases course
] as const;

export type EnrollmentSource = (typeof ENROLLMENT_SOURCES)[number];

export interface EnrollmentSummary {
  courseId: string;
  totalEnrolled: number;
  activeCount: number;
  completedCount: number;
  droppedCount: number;
  completionRate: number;
  avgProgressPercent: number;
  /** Module with highest drop-off */
  dropOffModule: { moduleId: string; dropOffPercent: number } | null;
}

/**
 * Compute enrollment summary statistics from enrollment data.
 */
export function computeEnrollmentSummary(
  courseId: string,
  enrollments: Array<{ status: string; progressPercent: number; lastModuleId?: string }>,
  moduleIds: string[],
): EnrollmentSummary {
  const total = enrollments.length;
  const active = enrollments.filter(e => e.status === "active").length;
  const completed = enrollments.filter(e => e.status === "completed").length;
  const dropped = enrollments.filter(e => e.status === "dropped").length;
  const avgProgress = total > 0
    ? Math.round(enrollments.reduce((s, e) => s + e.progressPercent, 0) / total)
    : 0;

  // Find drop-off module
  const moduleDropCounts = new Map<string, number>();
  for (const e of enrollments.filter(e => e.status === "dropped" && e.lastModuleId)) {
    const count = moduleDropCounts.get(e.lastModuleId!) ?? 0;
    moduleDropCounts.set(e.lastModuleId!, count + 1);
  }
  let dropOffModule: EnrollmentSummary["dropOffModule"] = null;
  let maxDrops = 0;
  for (const [moduleId, count] of moduleDropCounts) {
    if (count > maxDrops) {
      maxDrops = count;
      dropOffModule = { moduleId, dropOffPercent: total > 0 ? Math.round((count / total) * 100) : 0 };
    }
  }

  return {
    courseId,
    totalEnrolled: total,
    activeCount: active,
    completedCount: completed,
    droppedCount: dropped,
    completionRate: total > 0 ? Math.round((completed / total) * 100) : 0,
    avgProgressPercent: avgProgress,
    dropOffModule,
  };
}
